import api from './api';
import type { WineName } from './wineNameService';

// ── Enum types ────────────────────────────────────────────────────────────────
export type VintageType = 'single_vintage' | 'non_vintage' | 'multi_vintage';

// ── Vintage parsing utility ───────────────────────────────────────────────────

export interface ParsedVintage {
  vintageType: VintageType;
  vintage?: number;       // year for single_vintage
  baseVintage?: number;   // starting year for multi_vintage
  displayVintage: string; // "2018", "NV", "MV"
  valid: boolean;
  error?: string;
}

export function parseVintageInput(input: string): ParsedVintage {
  const trimmed = input.trim().toUpperCase();

  if (trimmed === 'NV') {
    return { vintageType: 'non_vintage', displayVintage: 'NV', valid: true };
  }

  if (trimmed === 'MV') {
    return { vintageType: 'multi_vintage', displayVintage: 'MV', valid: true };
  }

  const year = parseInt(trimmed, 10);
  if (!isNaN(year) && /^\d{4}$/.test(trimmed)) {
    if (year < 1800 || year > new Date().getFullYear() + 1) {
      return {
        vintageType: 'single_vintage',
        vintage: year,
        displayVintage: trimmed,
        valid: false,
        error: `Year ${year} is out of range`,
      };
    }
    return {
      vintageType: 'single_vintage',
      vintage: year,
      displayVintage: trimmed,
      valid: true,
    };
  }

  return {
    vintageType: 'single_vintage',
    displayVintage: trimmed,
    valid: false,
    error: 'Enter a 4-digit year (e.g. 2018), "NV" for non-vintage, or "MV" for multi-vintage',
  };
}

export function formatVintageInput(wine: Wine): string {
  if (wine.vintageType === 'non_vintage') return 'NV';
  if (wine.vintageType === 'multi_vintage') return 'MV';
  return wine.vintage ? String(wine.vintage) : '';
}

// ── Interfaces ────────────────────────────────────────────────────────────────

export interface WineWineName {
  _id: string;
  displayName: string;
  producer?: { _id: string; name: string; fullName?: string };
  region?: { _id: string; name: string; hierarchyNames?: string[] };
  color?: string;
  type?: string;
  needsReview?: boolean;
}

export interface Wine {
  _id: string;
  wineName: WineWineName;
  vintageType: VintageType;
  vintage?: number;
  baseVintage?: number;
  displayVintage: string;
  name: string; // generated & stored: wineName.displayName + " " + displayVintage
  active: boolean;
  needsReview?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface WineFormData {
  wineNameId: string;
  vintageType: VintageType;
  vintage?: number;
  baseVintage?: number;
  active: boolean;
  needsReview?: boolean;
}

export interface DuplicateCheckResult {
  isDuplicate: boolean;
  existing?: Wine;
}

/**
 * Lightweight projection returned by GET /admin/wine/search.
 * Differs from the full Wine record: uses wineDisplay instead of populated wineName.
 */
export interface WineSearchResult {
  _id: string;
  name: string;
  displayVintage: string;
  vintageType: VintageType;
  wineDisplay?: {
    producerName?: string;
    regionName?: string;
    color?: string;
  };
}

export interface PaginationMeta {
  currentPage: number;
  pageSize: number;
  totalPages: number;
  totalItems: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: PaginationMeta;
}

// ── Service ───────────────────────────────────────────────────────────────────

const WINE = '/admin/wine';
const WINE_NAME = '/admin/wine-name';

const wineService = {
  getAll: async (params?: {
    page?: number;
    pageSize?: number;
    search?: string;
    vintageType?: VintageType;
    active?: boolean;
    needsReview?: boolean;
  }): Promise<PaginatedResponse<Wine>> => {
    const response = await api.get(WINE, { params });
    return response.data;
  },

  getById: async (id: string): Promise<Wine> => {
    const response = await api.get(`${WINE}/${id}`);
    return response.data;
  },

  create: async (data: WineFormData): Promise<Wine> => {
    const response = await api.post(WINE, data);
    return response.data;
  },

  update: async (id: string, data: Partial<WineFormData>): Promise<Wine> => {
    const response = await api.put(`${WINE}/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`${WINE}/${id}`);
  },

  checkDuplicate: async (data: {
    wineNameId: string;
    vintageType: VintageType;
    vintage?: number;
  }): Promise<DuplicateCheckResult> => {
    const response = await api.post(`${WINE}/check-duplicate`, data);
    return response.data;
  },

  /**
   * Autocomplete search across Wine records.
   *
   * When `venueId` is provided, hits the venue-scoped lookup endpoint
   *   GET /admin/venue/:venueId/wine-lookup/search
   * which is open to VENUE_OPS_ROLES (read-only). Without `venueId` we fall
   * back to the global /admin/wine/search (admin-only) — used by admin
   * tooling outside the venue workspace.
   *
   * Min query length: 2 chars enforced client-side. Normalises response:
   * handles flat Wine[] or paginated { data: Wine[] } envelope.
   */
  search: async (q: string, pageSize = 10, venueId?: string): Promise<WineSearchResult[]> => {
    const url = venueId
      ? `/admin/venue/${venueId}/wine-lookup/search`
      : `${WINE}/search`;
    const response = await api.get(url, { params: { q: q.trim(), pageSize } });
    const raw = response.data;
    // Backend returns { data: WineSearchResult[] } envelope
    if (Array.isArray(raw?.data)) return raw.data;
    if (Array.isArray(raw)) return raw;
    return [];
  },

  searchWineNames: async (q: string): Promise<WineWineName[]> => {
    const PAGE_SIZE = 100;
    // Normalize: collapse all whitespace variants and invisible chars to a single space
    const normalize = (s: string) =>
      s.replace(/[\u00A0\u200B\uFEFF\t\r\n]+/g, ' ').replace(/\s+/g, ' ').trim();

    const trimmed = normalize(q);
    const lower = trimmed.toLowerCase();

    const mapWn = (wn: any): WineWineName => ({
      _id: wn._id,
      displayName: wn.displayName,
      producer: wn.producer,
      region: wn.region,
      color: wn.color,
      type: wn.type,
      needsReview: wn.needsReview,
    });

    const fetchPage = async (search: string, page: number) => {
      const response = await api.get(WINE_NAME, {
        params: { search, pageSize: PAGE_SIZE, page },
      });
      return response.data;
    };

    const exactMatch = (wn: WineWineName) =>
      normalize(wn.displayName).toLowerCase() === lower;

    // Strip parenthetical aliases from the search term so the backend doesn't
    // choke on special characters, e.g. "(Hubert Lignier)" → use words before it.
    const stripped = trimmed.replace(/\(.*?\)/g, ' ').replace(/\s+/g, ' ').trim();
    const words = stripped.split(/\s+/);

    // Progressively shorter search terms — stop as soon as backend returns results
    const candidates = [
      trimmed,
      stripped,
      words.slice(0, 3).join(' '),
      words.slice(0, 2).join(' '),
      words[0],
    ].filter((v, i, arr) => v.length > 0 && arr.indexOf(v) === i);

    for (const searchTerm of candidates) {
      let page = 1;
      const allMapped: WineWineName[] = [];

      while (true) {
        const data = await fetchPage(searchTerm, page);
        const mapped: WineWineName[] = (data?.data ?? []).map(mapWn);
        allMapped.push(...mapped);

        const exact = allMapped.find(exactMatch);
        if (exact) return [exact];

        const { totalPages } = data?.pagination ?? {};
        if (!totalPages || page >= totalPages) break;
        page++;
      }

      if (allMapped.length > 0) return allMapped;
    }

    return [];
  },
};

export default wineService;
